<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import axios from 'axios'
import { useSnackbar } from '@/composables/useSnackbar'
import ParcelClaimDialog from '@/components/shipment/claim-parcel/ParcelClaimDialog.vue'

const { t } = useI18n()

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Parcels',
    pageTitle: 'Claim Parcel',
    breadcrumb: [
      { title: 'Home', to: '/' },
      { title: 'Shipment', to: '/shipment' },
      { title: 'Claim Parcel', active: true },
    ],
  },
})

const { success, error, warning } = useSnackbar()
const route = useRoute()

// --- inputs ---
const mode      = ref('single') // 'single' | 'bulk' | 'unclaimed'
const single    = ref('')
const bulkBlob  = ref('')
const looking   = ref(false)

// --- unclaimed tab ---
const unclaimedRows     = ref([])
const unclaimedTotal    = ref(0)
const unclaimedLoading  = ref(false)
const unclaimedPage     = ref(1)
const unclaimedPerPage  = ref(25)
const unclaimedSearch   = ref('')
const unclaimedSelected = ref([])

const unclaimedHeaders = computed(() => [
  { title: t('shipment.barcode'),                 key: 'barcode' },
  { title: t('shipment.bag_code'),                key: 'bag_code' },
  { title: t('shipment.team'),                    key: 'team',      sortable: false },
  { title: t('shipment.container'),               key: 'container', sortable: false },
  { title: t('shipment.status'),                  key: 'status',    sortable: false },
  { title: t('actions.actions'),                  key: 'actions',   sortable: false, align: 'end' },
])

let unclaimedSearchTimer = null

async function loadUnclaimed() {
  unclaimedLoading.value = true
  try {
    const { data } = await axios.get('shipment/incoming-parcels/unclaimed', {
      params: {
        q: unclaimedSearch.value?.trim() || undefined,
        page: unclaimedPage.value,
        // eslint-disable-next-line camelcase
        per_page: unclaimedPerPage.value,
      },
    })

    unclaimedRows.value  = data.data ?? []
    unclaimedTotal.value = data.total ?? 0
  } catch (err) {
    console.error('loadUnclaimed failed', err)
    unclaimedRows.value = []
    unclaimedTotal.value = 0
  } finally {
    unclaimedLoading.value = false
  }
}

function handleUnclaimedTable(opts) {
  unclaimedPage.value = opts.page ?? 1
  unclaimedPerPage.value = opts.itemsPerPage ?? 25
  loadUnclaimed()
}

// Debounce the unclaimed search input.
watch(unclaimedSearch, () => {
  clearTimeout(unclaimedSearchTimer)
  unclaimedSearchTimer = setTimeout(() => {
    unclaimedPage.value = 1
    loadUnclaimed()
  }, 300)
})

// Lazy-load when the tab is first opened.
watch(mode, val => {
  if (val === 'unclaimed' && unclaimedRows.value.length === 0 && !unclaimedLoading.value) {
    loadUnclaimed()
  }
})

// Single-attach + bulk-attach from the unclaimed tab use the shared
// ClaimParcelDialog state.
const claimDialogOpen     = ref(false)
const claimDialogMode     = ref('single')
const claimDialogParcel   = ref(null)
const claimDialogParcelIds = ref([])

function openAttachUnclaimed(item) {
  claimDialogMode.value = 'single'
  claimDialogParcel.value = item
  claimDialogParcelIds.value = []
  claimDialogOpen.value = true
}

function openBulkAttachUnclaimed() {
  if (!unclaimedSelected.value.length) return
  claimDialogMode.value = 'bulk'
  claimDialogParcel.value = null
  claimDialogParcelIds.value = [...unclaimedSelected.value]
  claimDialogOpen.value = true
}

function onUnclaimedClaimDone() {
  unclaimedSelected.value = []
  loadUnclaimed()
}

// If we got here with ?customer_id= (from the customer detail page) or
// ?target_contact_id= (from the chatbot-conflict bell "Reassign to claimer"
// shortcut), preload that customer into the dialog so the team doesn't have
// to re-search them.
const preloadedCustomerId = computed(() => {
  const v = route.query.customer_id ?? route.query.target_contact_id

  return v ? Number(v) : null
})

const preloadedCustomer = ref(null)

onMounted(async () => {
  // 1) Preload the target customer if we have an id in the query.
  if (preloadedCustomerId.value) {
    try {
      const { data } = await axios.get(`shipment/customers/${preloadedCustomerId.value}`)

      preloadedCustomer.value = data?.data ?? data ?? null
    } catch { /* non-fatal — user can still search manually */ }
  }

  // 2) If we also got a ?barcode= (chatbot-conflict deep link), prefill the
  //    single-barcode search and run the lookup so the operator lands on the
  //    results row in one step. When the parcel comes back already attached
  //    AND we have a target customer to reassign to, auto-open the Reassign
  //    dialog with that customer pre-selected — operator just types the note
  //    and confirms.
  const qBarcode = route.query.barcode
  if (qBarcode && typeof qBarcode === 'string') {
    mode.value = 'single'
    single.value = qBarcode
    await lookup()

    if (route.query.target_contact_id) {
      const q = queries.value.find(x => x.found && x.parcel?.customer)
      if (q) openReassign(q)
    }
  }
})

// --- results ---
const queries  = ref([])  // [{ input, cleaned, found, parcel?, message? }]
const summary  = ref(null)

// --- claim dialog ---
const dialogOpen = ref(false)
const dialogMode = ref('attach')          // 'attach' | 'reassign'
const dialogParcel = ref(null)
const dialogParcelIds = ref([])           // for bulk attach

// --- fire lookup ---
async function lookup() {
  looking.value = true
  try {
    const payload = mode.value === 'bulk'
      // eslint-disable-next-line camelcase
      ? { barcodes_blob: bulkBlob.value }
      : { barcode: single.value }

    const { data } = await axios.post('shipment/claims/lookup', payload)

    queries.value = data.queries || []
    summary.value = data.summary || null
  } catch (e) {
    error(e?.response?.data?.message || e.message)
  } finally {
    looking.value = false
  }
}

function reset() {
  queries.value = []
  summary.value = null
  single.value = ''
  bulkBlob.value = ''
}

// --- per-row actions ---
function openAttach(q) {
  dialogMode.value = 'attach'
  dialogParcel.value = q.parcel
  dialogParcelIds.value = []
  dialogOpen.value = true
}

function openReassign(q) {
  dialogMode.value = 'reassign'
  dialogParcel.value = q.parcel
  dialogParcelIds.value = []
  dialogOpen.value = true
}

// --- bulk attach all "free" rows to a single customer ---
const freeIds = computed(() =>
  queries.value
    .filter(q => q.found && q.parcel && !q.parcel.customer)
    .map(q => q.parcel.id),
)

function openBulkAttachFree() {
  if (!freeIds.value.length) {
    warning(t('shipment.claim_parcel.no_free_in_results'))
    
    return
  }
  dialogMode.value = 'attach'
  dialogParcel.value = null
  dialogParcelIds.value = freeIds.value
  dialogOpen.value = true
}

function onClaimDone() {
  // Re-run the lookup so the table reflects the new customer attachments.
  success(t('shipment.claim_parcel.updated_refreshing'))
  lookup()
}
</script>

<template>
  <section>
    <div class="d-flex align-center justify-space-between mb-4 flex-wrap gap-2">
      <div>
        <h4 class="text-h4">
          {{ t('shipment.claim_parcel.title') }}
        </h4>
        <p class="text-body-2 text-medium-emphasis mb-0">
          {{ t('shipment.claim_parcel.subtitle') }}
        </p>
      </div>
    </div>

    <!-- Lookup card -->
    <VCard class="mb-4">
      <VCardText>
        <VTabs
          v-model="mode"
          grow
          class="mb-4"
        >
          <VTab value="single">
            <VIcon
              icon="tabler-barcode"
              start
            /> {{ t('shipment.claim_parcel.tab_single') }}
          </VTab>
          <VTab value="bulk">
            <VIcon
              icon="tabler-stack"
              start
            /> {{ t('shipment.claim_parcel.tab_bulk') }}
          </VTab>
          <VTab value="unclaimed">
            <VIcon
              icon="tabler-inbox"
              start
            />
            {{ t('shipment.claim_parcel.tab_unclaimed') }}
            <VChip
              v-if="unclaimedTotal > 0"
              size="x-small"
              color="error"
              variant="tonal"
              class="ms-2"
            >
              {{ unclaimedTotal }}
            </VChip>
          </VTab>
        </VTabs>

        <VWindow v-model="mode">
          <VWindowItem value="single">
            <div class="d-flex gap-2">
              <VTextField
                v-model="single"
                :label="t('shipment.claim_parcel.single_label')"
                density="compact"
                :placeholder="t('shipment.claim_parcel.single_placeholder')"
                @keyup.enter="lookup"
              />
              <VBtn
                color="primary"
                :loading="looking"
                :disabled="!single"
                @click="lookup"
              >
                <VIcon
                  icon="tabler-search"
                  start
                /> {{ t('shipment.claim_parcel.lookup') }}
              </VBtn>
            </div>
          </VWindowItem>

          <VWindowItem value="bulk">
            <p class="text-caption text-medium-emphasis mb-2">
              {{ t('shipment.claim_parcel.bulk_hint') }}
            </p>
            <VTextarea
              v-model="bulkBlob"
              :label="t('shipment.claim_parcel.bulk_label')"
              rows="4"
              density="compact"
              placeholder="YT2543123456789&#10;YT2543123456790&#10;YT2543123456791"
            />
            <div class="d-flex justify-end mt-2">
              <VBtn
                color="primary"
                :loading="looking"
                :disabled="!bulkBlob.trim()"
                @click="lookup"
              >
                <VIcon
                  icon="tabler-search"
                  start
                /> {{ t('shipment.claim_parcel.lookup_all') }}
              </VBtn>
            </div>
          </VWindowItem>

          <!--
            Unattached parcels tab — physically in the warehouse but
            with no customer yet. Source: GET /shipment/incoming-parcels/unclaimed.
            Same filter as the sidebar Inbox badge. 
          -->
          <VWindowItem value="unclaimed">
            <div class="d-flex align-center gap-2 mb-3 flex-wrap">
              <VTextField
                v-model="unclaimedSearch"
                :label="t('shipment.claim_parcel.unclaimed_search')"
                :placeholder="t('shipment.claim_parcel.unclaimed_search_placeholder')"
                prepend-inner-icon="tabler-search"
                density="compact"
                clearable
                hide-details
                style="max-inline-size: 360px;"
              />
              <VSpacer />
              <VBtn
                v-if="unclaimedSelected.length > 0"
                color="primary"
                prepend-icon="tabler-link"
                @click="openBulkAttachUnclaimed"
              >
                {{ t('claim_dialog.bulk_action') }} ({{ unclaimedSelected.length }})
              </VBtn>
              <VBtn
                icon="tabler-refresh"
                variant="text"
                size="small"
                :title="t('common.refresh')"
                @click="loadUnclaimed"
              />
            </div>

            <VDataTableServer
              v-model="unclaimedSelected"
              :headers="unclaimedHeaders"
              :items="unclaimedRows"
              :items-length="unclaimedTotal"
              :loading="unclaimedLoading"
              :page="unclaimedPage"
              :items-per-page="unclaimedPerPage"
              item-value="id"
              show-select
              density="compact"
              @update:options="handleUnclaimedTable"
            >
              <template #item.barcode="{ item }">
                <RouterLink
                  :to="{ name: 'shipment-incoming-parcels-id', params: { id: item.id } }"
                  class="text-primary text-decoration-none"
                >
                  <code>{{ item.barcode || '—' }}</code>
                </RouterLink>
              </template>
              <template #item.bag_code="{ item }">
                <VChip
                  v-if="item.bag_code"
                  size="x-small"
                  variant="tonal"
                  color="warning"
                >
                  {{ item.bag_code }}
                </VChip>
                <span
                  v-else
                  class="text-disabled"
                >—</span>
              </template>
              <template #item.team="{ item }">
                <TeamBadge
                  v-if="item.team_code"
                  :team="{ code: item.team_code, color: item.team_color }"
                />
                <span
                  v-else
                  class="text-disabled"
                >—</span>
              </template>
              <template #item.container="{ item }">
                <VChip
                  v-if="item.container_reference"
                  size="x-small"
                  variant="outlined"
                  color="info"
                  prepend-icon="tabler-ship"
                  :to="item.container_id ? { name: 'history-containers-detail', params: { containerId: item.container_id } } : undefined"
                  @click.stop
                >
                  {{ item.container_reference }}
                </VChip>
                <span
                  v-else
                  class="text-disabled"
                >—</span>
              </template>
              <template #item.status="{ item }">
                <ParcelStatusChip
                  :status="item.status"
                  size="x-small"
                />
              </template>
              <template #item.actions="{ item }">
                <VBtn
                  size="small"
                  color="primary"
                  variant="text"
                  prepend-icon="tabler-link"
                  @click="openAttachUnclaimed(item)"
                >
                  {{ t('claim_dialog.claim_action') }}
                </VBtn>
              </template>
            </VDataTableServer>
          </VWindowItem>
        </VWindow>
      </VCardText>
    </VCard>

    <!-- Results -->
    <VCard v-if="queries.length || summary">
      <VCardTitle class="d-flex align-center">
        <span>{{ t('shipment.claim_parcel.results') }}</span>
        <VSpacer />
        <VBtn
          variant="text"
          size="small"
          @click="reset"
        >
          <VIcon
            icon="tabler-x"
            start
          /> {{ t('shipment.claim_parcel.clear') }}
        </VBtn>
      </VCardTitle>

      <VCardText>
        <!-- Summary chips -->
        <div
          v-if="summary"
          class="d-flex gap-2 flex-wrap mb-4"
        >
          <VChip
            color="default"
            variant="tonal"
            size="small"
          >
            <VIcon
              icon="tabler-list"
              start
            /> {{ t('shipment.claim_parcel.summary_total', { count: summary.total }) }}
          </VChip>
          <VChip
            color="success"
            variant="tonal"
            size="small"
          >
            <VIcon
              icon="tabler-check"
              start
            /> {{ t('shipment.claim_parcel.summary_found', { count: summary.found }) }}
          </VChip>
          <VChip
            color="primary"
            variant="tonal"
            size="small"
          >
            <VIcon
              icon="tabler-link-off"
              start
            /> {{ t('shipment.claim_parcel.summary_free', { count: summary.free }) }}
          </VChip>
          <VChip
            color="warning"
            variant="tonal"
            size="small"
          >
            <VIcon
              icon="tabler-link"
              start
            /> {{ t('shipment.claim_parcel.summary_already_attached', { count: summary.already_attached }) }}
          </VChip>
          <VChip
            color="error"
            variant="tonal"
            size="small"
          >
            <VIcon
              icon="tabler-question-mark"
              start
            /> {{ t('shipment.claim_parcel.summary_not_found', { count: summary.not_found }) }}
          </VChip>
          <VSpacer />
          <VBtn
            v-if="freeIds.length > 1"
            color="primary"
            size="small"
            prepend-icon="tabler-users-plus"
            @click="openBulkAttachFree"
          >
            {{ t('shipment.claim_parcel.attach_free_to_one', { count: freeIds.length }) }}
          </VBtn>
        </div>

        <VTable hover>
          <thead>
            <tr>
              <th>{{ t('shipment.claim_parcel.col_input') }}</th>
              <th>{{ t('shipment.claim_parcel.col_cleaned') }}</th>
              <th>{{ t('shipment.claim_parcel.col_status') }}</th>
              <th>{{ t('shipment.claim_parcel.col_customer') }}</th>
              <th>{{ t('shipment.claim_parcel.col_team') }}</th>
              <th class="text-end">
                {{ t('shipment.claim_parcel.col_actions') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(q, idx) in queries"
              :key="idx"
            >
              <td><code class="text-caption">{{ q.input }}</code></td>
              <td>
                <code
                  v-if="q.cleaned"
                  class="text-caption"
                >{{ q.cleaned }}</code>
                <span
                  v-else
                  class="text-medium-emphasis"
                >—</span>
              </td>
              <td>
                <VChip
                  v-if="q.found && q.parcel?.customer"
                  color="warning"
                  variant="tonal"
                  size="small"
                >
                  {{ t('shipment.claim_parcel.status_already_attached') }}
                </VChip>
                <VChip
                  v-else-if="q.found"
                  color="primary"
                  variant="tonal"
                  size="small"
                >
                  {{ t('shipment.claim_parcel.status_free') }}
                </VChip>
                <VChip
                  v-else
                  color="error"
                  variant="tonal"
                  size="small"
                >
                  {{ t('shipment.claim_parcel.status_not_found') }}
                </VChip>
              </td>
              <td>
                <div v-if="q.parcel?.customer">
                  <CustomerLink
                    :customer="q.parcel.customer"
                    :show-client-id="false"
                  />
                  <div class="text-caption text-medium-emphasis">
                    {{ q.parcel.customer.anonymized_code || q.parcel.customer.phone || '' }}
                  </div>
                </div>
                <span
                  v-else
                  class="text-medium-emphasis"
                >—</span>
              </td>
              <td>
                <TeamBadge
                  :team="q.parcel?.team"
                  variant="outlined"
                />
              </td>
              <td class="text-end">
                <template v-if="q.found && q.parcel">
                  <VBtn
                    v-if="!q.parcel.customer"
                    size="small"
                    color="primary"
                    variant="tonal"
                    prepend-icon="tabler-link"
                    @click="openAttach(q)"
                  >
                    {{ t('shipment.claim_parcel.attach') }}
                  </VBtn>
                  <VBtn
                    v-else
                    size="small"
                    color="warning"
                    variant="tonal"
                    prepend-icon="tabler-arrows-exchange"
                    @click="openReassign(q)"
                  >
                    {{ t('shipment.claim_parcel.reassign') }}
                  </VBtn>
                </template>
                <span
                  v-else-if="q.message"
                  class="text-caption text-medium-emphasis"
                >
                  {{ q.message }}
                </span>
              </td>
            </tr>
          </tbody>
        </VTable>
      </VCardText>
    </VCard>

    <ParcelClaimDialog
      v-model="dialogOpen"
      :mode="dialogMode"
      :parcel="dialogParcel"
      :parcel-ids="dialogParcelIds"
      :preloaded-customer="preloadedCustomer"
      @done="onClaimDone"
    />

    <!--
      Shared dialog used by the new "Unattached parcels" tab.
      (The existing ParcelClaimDialog above expects a different shape
      from the lookup flow; ClaimParcelDialog is the unified one we
      added on 2026-05-24.) 
    -->
    <ClaimParcelDialog
      v-model="claimDialogOpen"
      :mode="claimDialogMode"
      :parcel-id="claimDialogParcel?.id"
      :barcode="claimDialogParcel?.barcode"
      :current-customer-id="claimDialogParcel?.customer?.id"
      :current-customer-name="claimDialogParcel?.customer?.name"
      :parcel-ids="claimDialogParcelIds"
      @done="onUnclaimedClaimDone"
    />

    <!-- Hint banner when arrived via customer-detail entry point. -->
    <VAlert
      v-if="preloadedCustomer"
      type="info"
      variant="tonal"
      density="compact"
      class="mt-4"
    >
      {{ t('shipment.claim_parcel.context_hint_prefix') }} <strong>{{ preloadedCustomer.name }}</strong><span v-if="preloadedCustomer.anonymized_code"> ({{ preloadedCustomer.anonymized_code }})</span>.
      {{ t('shipment.claim_parcel.context_hint_suffix') }}
    </VAlert>
  </section>
</template>
